import * as React from "react";
import { cn } from "@/lib/utils";
import './animated-button.css';

export interface AnimatedButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "success" | "neutral";
  asChild?: boolean;
  children?: React.ReactNode;
}

const AnimatedButton = React.forwardRef<HTMLButtonElement, AnimatedButtonProps>(
  ({ className, variant = "primary", children, onClick, ...props }, ref) => {
    const [isProcessing, setIsProcessing] = React.useState(false);
    const [isReverting, setIsReverting] = React.useState(false);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isProcessing || isReverting) return;
      
      setIsProcessing(true);
      
      // Call the original onClick handler
      if (onClick) {
        onClick(e);
      }

      // Start reverting after animation completes
      setTimeout(() => {
        setIsProcessing(false);
        setIsReverting(true);
        
        setTimeout(() => {
          setIsReverting(false);
        }, 2000);
      }, 6600);
    };

    const variantClasses = {
      primary: "animated-button--primary",
      secondary: "animated-button--secondary", 
      success: "animated-button--success",
      neutral: "animated-button--neutral"
    };

    return (
      <button
        ref={ref}
        className={cn(
          "animated-button",
          variantClasses[variant],
          isProcessing && "s--processing",
          isReverting && "s--reverting",
          className
        )}
        onClick={handleClick}
        {...props}
      >
        <span className="animated-button-transitionend-listener"></span>
        <div className="animated-button__revert-line"></div>
        
        <div className="animated-button__drone-cont animated-button__drone-cont--takeoff">
          <div className="animated-button__drone-cont animated-button__drone-cont--shift-x">
            <div className="animated-button__drone-cont animated-button__drone-cont--landing">
              <svg className="animated-button__drone animated-button__drone-leaving" viewBox="0 0 136 112">
                <g className="animated-button__drone-leaving">
                  <path
                    className="animated-button__drone-arm animated-button__drone-arm--1"
                    d="M68,56 C38.5,56 22.5,70.5 22.5,70.5 L22.5,46.5 C22.5,46.5 38.5,32 68,32"
                  />
                  <path
                    className="animated-button__drone-arm animated-button__drone-arm--2"
                    d="M68,56 C38.5,56 22.5,70.5 22.5,70.5 L22.5,46.5 C22.5,46.5 38.5,32 68,32"
                  />
                  <path className="animated-button__drone-green" d="M70,0 L102,16 L102,40 L70,56 L38,40 L38,16 Z" />
                  <path d="M70,0 L102,16 L102,40 L70,56 L38,40 L38,16 Z" />
                  <path
                    className="animated-button__drone-yellow"
                    d="M60,56 C60,58.2 61.8,60 64,60 L76,60 C78.2,60 80,58.2 80,56 L80,56 L60,56 Z"
                  />
                  <rect
                    className="animated-button__drone-package"
                    x="64"
                    y="68"
                    width="12"
                    height="16"
                    rx="2"
                  />
                </g>
              </svg>
            </div>
          </div>
        </div>

        <div className="animated-button__circle">
          <div className="animated-button__circle-inner">
            <svg className="animated-button__circle-package" viewBox="0 0 14 18">
              <path d="M0,0 L7,4 L7,18 M14,0 L7,4 L7,18 M14,0 L7,4 L0,0 L0,9" />
            </svg>
            <div className="animated-button__circle-grabbers"></div>
          </div>
          <svg className="animated-button__circle-progress" viewBox="-21 -21 42 42">
            <circle
              className="animated-button__circle-progress-line"
              r="20"
            />
            <polyline
              className="animated-button__circle-progress-checkmark"
              points="-6,0 -2,4 6,-4"
              stroke-linecap="round"
            />
          </svg>
        </div>

        <div className="animated-button__text-fields">
          <div className="animated-button__text animated-button__text--step-0">
            {children}
          </div>
          <div className="animated-button__text animated-button__text--step-1">
            Preparing<span className="animated-button__text-dots"><span>.</span></span>
          </div>
          <div className="animated-button__text animated-button__text--step-2">
            Processing<span className="animated-button__text-dots"><span>.</span></span>
          </div>
          <div className="animated-button__text animated-button__text--step-3">
            Finalizing<span className="animated-button__text-dots"><span>.</span></span>
          </div>
          <div className="animated-button__text animated-button__text--step-4">Complete!</div>
        </div>
      </button>
    );
  }
);

AnimatedButton.displayName = "AnimatedButton";

export { AnimatedButton };
